import { useCallback } from 'react';

import { useCluster } from '@cast/shared-cluster-context-data-access';
import {
  useEvictorConfigMutation,
  useEvictorAdvancedConfigMutation,
} from '@cast/shared-workload-eviction-data-access';
import {
  EvictorConfig,
  EvictorAdvancedConfig,
} from '@cast/types-workload-eviction';
import isEqual from 'lodash/isEqual';

type SubmitParams = {
  initialConfig: EvictorConfig;
  initialAdvancedConfig: EvictorAdvancedConfig;
  config: EvictorConfig;
  advancedConfig: EvictorAdvancedConfig;
};

type SubmitCallbacks = {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
};

const hasConfigChanged = (
  initialConfig: EvictorConfig | EvictorAdvancedConfig,
  updatedConfig: EvictorConfig | EvictorAdvancedConfig
) => !isEqual(initialConfig, updatedConfig);

export const useSaveEvictorSettings = () => {
  const { cluster } = useCluster();

  const configMutation = useEvictorConfigMutation();
  const advancedConfigMutation = useEvictorAdvancedConfigMutation();

  const submit = useCallback(
    async (params: SubmitParams, callbacks?: SubmitCallbacks) => {
      const configChanged = hasConfigChanged(
        params.initialConfig,
        params.config
      );
      const advancedConfigChanged = hasConfigChanged(
        params.initialAdvancedConfig,
        params.advancedConfig
      );

      const promises: Promise<unknown>[] = [];

      if (configChanged) {
        promises.push(
          configMutation.mutateAsync({
            clusterId: cluster.id,
            config: params.config,
          })
        );
      }

      if (advancedConfigChanged) {
        promises.push(
          advancedConfigMutation.mutateAsync({
            clusterId: cluster.id,
            advancedConfig: params.advancedConfig,
          })
        );
      }

      try {
        await Promise.all(promises);
        callbacks?.onSuccess?.();
      } catch (error) {
        callbacks?.onError?.(error);
      }
    },
    [cluster.id, configMutation, advancedConfigMutation]
  );

  return {
    submit,
    isSaving: configMutation.isPending || advancedConfigMutation.isPending,
  };
};
